/**
 * Tipos internos da tela de orçamentos.
 *
 * Papel: aqui ficam os formatos que o react-hook-form guarda para o orçamento e
 * para o item, e os dois valores em branco de onde a criação parte.
 *
 * Os dois são derivados dos schemas do contrato com `z.input`, e não escritos à
 * mão, porque é a entrada daqueles schemas que o formulário precisa produzir.
 */
import { type novoItemOrcamentoSchema, type novoOrcamentoSchema } from '@concierge/shared';
import { type z } from 'zod';

/** O orçamento como o formulário o guarda, antes de o schema normalizar. */
export type OrcamentoNoFormulario = z.input<typeof novoOrcamentoSchema>;

/** O item como o formulário o guarda. */
export type ItemNoFormulario = z.input<typeof novoItemOrcamentoSchema>;

/**
 * Um item em branco, como uma linha recém-acrescentada começa.
 *
 * Não há quantidade: `valor` é o valor cheio da linha, e é isso que separa esta
 * lista da de um pedido de compra.
 *
 * `foto` fica **ausente**, e não vazia: ausente significa "não mexa", e é o que
 * preserva a imagem de um item já gravado quando o formulário o reenvia. Uma
 * linha nova também não tem `id`, e é por isso que ela nasce sem imagem — a API
 * casa a imagem pela identidade do item, nunca pela posição dele na lista.
 */
export const ITEM_EM_BRANCO: ItemNoFormulario = {
  descricao: '',
  referencia: '',
  valor: '',
};

/**
 * Um orçamento em branco, como o formulário de criação começa.
 *
 * `status` começa em `orcamento`, que é onde a proposta nasce: aprovada,
 * recusada ou cancelada são desfechos, e nenhum deles vale como ponto de
 * partida.
 *
 * `categoria` começa em `outros`, que é o mesmo padrão da coluna no banco. Quem
 * não abrir o seletor grava `outros` sem ter escolhido nada, e é por isso que
 * essa categoria reúne o que não coube e o que ninguém classificou.
 *
 * `itens` começa com uma linha, e não vazio: um orçamento sem item nenhum é
 * válido — é assim que se registra a proposta que ainda vai chegar —, mas quem
 * abre o formulário quase sempre já tem a proposta em mãos.
 */
export const ORCAMENTO_EM_BRANCO: OrcamentoNoFormulario = {
  residenciaId: '',
  numero: '',
  data: '',
  status: 'orcamento',
  categoria: 'outros',
  fornecedor: '',
  responsavel: '',
  solicitante: '',
  pagamento: 'a_vista',
  desconto: '',
  observacao: '',
  itens: [ITEM_EM_BRANCO],
};
